'use client';

import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MultiSelect } from '@/components/ui/multi-select';
import { ProcessorEditHeader } from '../../components/processor-edit-header';
import { SecretInput } from '@/components/form/secret-input';

import { ProcessorDetails, useProcessorById, useUpdateProcessor } from '@/lib/api/processors';
import { minorUnitsToMajorUnits, moneyStringOrNumberToMinorUnits } from '@/lib/money';
import { ProcessorDryModeField } from '../../components/processor-dry-mode-field';
import { ErrorDisplay } from '@/components/ui/error-display';
import { parseTRPCError } from '@/lib/utils/trpc-error-utils';
import { useToast } from '@/components/ui/use-toast';
import { useProcessorEditId } from '@/app/processors/hooks/use-processor-edit-id';
import { useProcessorEditExit } from '@/app/processors/hooks/use-processor-edit-exit';

const AVAILABLE_CURRENCIES = ['USD', 'EUR', 'GBP', 'CAD', 'AUD'];

const CURRENCY_OPTIONS = AVAILABLE_CURRENCIES.map((currency) => ({
  label: currency,
  value: currency,
}));

interface FinixEditPageProps {
  processor: ProcessorDetails;
}

const FinixEditPageContainerInner = () => {
  const { t } = useTranslation('processors');
  const id = useProcessorEditId();
  const { data, isLoading, error } = useProcessorById(id!);

  if (isLoading) {
    return <div className="container mx-auto py-8">{t('loading')}</div>;
  }

  if (error) {
    return <ErrorDisplay error={parseTRPCError(error)} />;
  }

  if (!data) {
    return <div className="container mx-auto py-8">{t('processorNotFound')}</div>;
  }

  return <FinixEditPage processor={data} />;
};

const FinixEditPage = ({ processor }: FinixEditPageProps) => {
  const { t } = useTranslation('processors');
  const navigate = useNavigate();
  const processorExit = useProcessorEditExit();
  const { toast } = useToast();

  const updateProcessorMutation = useUpdateProcessor();

  const [formData, setFormData] = useState({
    name: processor.name || '',
    enabled: processor.enabled ?? true,
    baseCurrency: processor.baseCurrency || 'USD',
    supportedCurrencies: processor.supportedCurrencies || ['USD'],
    monthlyProcessingCapacity: minorUnitsToMajorUnits(
      processor.monthlyProcessingCapacity || 0,
      processor.baseCurrency || 'USD',
    ),
    username: (processor.options as any)?.username || '',
    password: (processor.options as any)?.password || '',
    merchantId: (processor.options as any)?.merchantId || '',
    sandboxed: (processor.options as any)?.sandboxed ?? true,
    dryMode: (processor.options as any)?.dryMode === true,
    webhookSecret: (processor.options as any)?.webhookSecret || '',
  });

  const handleSubmit = async () => {
    // Ensure baseCurrency is in supportedCurrencies
    const supportedCurrencies = formData.supportedCurrencies.includes(formData.baseCurrency)
      ? formData.supportedCurrencies
      : [...formData.supportedCurrencies, formData.baseCurrency];

    try {
      await updateProcessorMutation.mutateAsync({
        processorId: processor.id,
        updatedData: {
          type: 'finix',
          name: formData.name,
          enabled: formData.enabled,
          baseCurrency: formData.baseCurrency,
          supportedCurrencies,
          monthlyProcessingCapacity: moneyStringOrNumberToMinorUnits(
            formData.monthlyProcessingCapacity,
            formData.baseCurrency,
          ),
          options: {
            username: formData.username,
            password: formData.password,
            merchantId: formData.merchantId,
            sandboxed: formData.sandboxed,
            dryMode: formData.dryMode,
            webhookSecret: formData.webhookSecret,
          },
        } as any,
      });

      toast({ description: t('processorUpdated') });
      if (processorExit.isEmbed) {
        processorExit.exitAfterSave();
        return;
      }
      navigate(-1);
    } catch (error) {
      console.error('Error saving processor:', error);
      toast({ description: t('failedToSave'), variant: 'destructive' });
    }
  };

  return (
    <div className="container mx-auto py-8">
      <ProcessorEditHeader
        processorId={processor.id}
        title={t('editFinixConfiguration')}
        onCancel={() => (processorExit.isEmbed ? processorExit.exitCancel() : navigate(-1))}
        onSave={handleSubmit}
        isSaving={updateProcessorMutation.isPending}
      />

      <div className="space-y-6">
        {/* General Settings */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t('generalSettings')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="name">{t('name')}</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('enterProcessorName')}
              />
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="enabled"
                checked={formData.enabled}
                onCheckedChange={(checked) => setFormData({ ...formData, enabled: checked === true })}
              />
              <Label htmlFor="enabled">{t('enabled')}</Label>
            </div>
          </CardContent>
        </Card>

        {/* Currency Settings */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t('currencySettings')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="supportedCurrencies">{t('supportedCurrencies')}</Label>
              <MultiSelect
                options={CURRENCY_OPTIONS}
                selected={formData.supportedCurrencies}
                onChange={(value) => setFormData({ ...formData, supportedCurrencies: value })}
                placeholder={t('searchForCurrency')}
              />
            </div>

            <div>
              <Label htmlFor="baseCurrency">{t('baseCurrency')}</Label>
              <Select
                value={formData.baseCurrency}
                onValueChange={(value) => setFormData({ ...formData, baseCurrency: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('selectBaseCurrency')} />
                </SelectTrigger>
                <SelectContent>
                  {AVAILABLE_CURRENCIES.map((currency) => (
                    <SelectItem key={currency} value={currency}>
                      {currency}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="monthlyProcessingCapacity">{t('monthlyProcessingCapacity')}</Label>
              <Input
                id="monthlyProcessingCapacity"
                type="number"
                min={0}
                value={formData.monthlyProcessingCapacity}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    monthlyProcessingCapacity: Math.max(0, Number(e.target.value)),
                  })
                }
                placeholder="0"
              />
              <p className="text-muted-foreground mt-1 text-xs">
                {t('monthlyProcessingCapacityDescription')}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Finix API Settings */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t('finixApiSettings')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="username">{t('apiUsername')}</Label>
              <Input
                id="username"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                placeholder={t('enterApiUsername')}
              />
              <p className="text-muted-foreground mt-1 text-xs">
                Your Finix API username
              </p>
            </div>

            <SecretInput
              label={t('apiPassword')}
              value={formData.password}
              onChange={(value) => setFormData({ ...formData, password: value })}
              placeholder={t('enterApiPassword')}
            />

            <div>
              <Label htmlFor="merchantId">{t('merchantId')}</Label>
              <Input
                id="merchantId"
                value={formData.merchantId}
                onChange={(e) => setFormData({ ...formData, merchantId: e.target.value })}
                placeholder={t('enterMerchantId')}
              />
              <p className="text-muted-foreground mt-1 text-xs">
                Your Finix merchant ID
              </p>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="sandboxed"
                checked={formData.sandboxed}
                onCheckedChange={(checked) => setFormData({ ...formData, sandboxed: checked === true })}
              />
              <Label htmlFor="sandboxed">{t('useSandboxMode')}</Label>
            </div>

            {formData.sandboxed && (
              <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3">
                <p className="text-sm text-yellow-800">
                  Sandbox mode enabled. Use test credentials and test card numbers.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Webhook Settings */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t('webhookSettings')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <SecretInput
              label={t('webhookSecret')}
              value={formData.webhookSecret}
              onChange={(value) => setFormData({ ...formData, webhookSecret: value })}
              placeholder={t('enterWebhookSecret')}
            />
            <p className="text-muted-foreground text-xs">
              Optional: Configure webhook secret for verifying webhook signatures from Finix
            </p>
          </CardContent>
        </Card>

                <ProcessorDryModeField
                    checked={formData.dryMode}
                    onCheckedChange={(checked) => setFormData({ ...formData, dryMode: checked })}
                    disabled={updateProcessorMutation.isPending}
                />
      </div>
    </div>
  );
};

export const FinixEditPageContainer = withRequiredPermission(FinixEditPageContainerInner, PERMISSIONS.PROCESSORS_MANAGE);
export default FinixEditPageContainer;
